import React, { useId, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { UserInvite } from './user-login-edit/user-invite'
import { UserLinkedAccounts } from './user-login-edit/user-linked-accounts'
import { HeatmapLayoutUser } from '../../../../../view/heatmap/layout-user'
import { useParamsInt } from '../../../../../shared/hooks/location'
import { IconEdit, IconRestore } from '../../../../../shared/icon/icon'
import { useApiFormUserLogin } from '../../../../../api-new/state/content'
import { Notice } from '../../../../../shared/sso-workspace/fleet-page'
import { ConfirmDialog } from '../../../../../shared/sso-workspace/confirm-dialog'
import { ErrorReference } from '../../../../../shared/error/error-reference'
import { recycleErrorMessage, serverMessage } from '../../../../../shared/error/server-message'
import { useErrorLine } from '../../../../../shared/error/use-error-line'
import { RoleOrgGroup, RoleOrgGroupUser, RoleOrgNode, RoleOrgNodeUser } from '../../../../../role/components/role-org'
import { useRoleOrgUser } from '../../../../../role/hook/role-org'
import { useNotificationContext } from '../../../../../context/notification/context'
import { contentDelete, contentGet, contentPut } from '../../../../../shared/email-activity/content-api'
import { Menu } from '../../../../../shared/menu/menu'
import { MenuHeaderContext } from '../../../../../shared/menu/menu-context'
import { personTitle } from './common/managed-by'
import { useUserMenuItems } from './common/user-menu-items'
import { restoreNotice } from './user-recycle'
import { PersonHeader } from './person/person-header'
import { PersonDetails } from './person/person-details'
import { RemovedCard } from './person/removed-card'
import { CoursesSection, GroupsSection, KeptNote, RolesSection } from './person/person-sections'
import { purgeDialog, removeDialog, restoreDialog } from './person/person-words'
import '../../../../../shared/sso-workspace/sso-workspace.scss'
import '../../../../../shared/sso-workspace/fleet.scss'
import './person/person-page.scss'

// Content answers 404 for the member's own read when they aren't a current
// member: removed (a member the partner API deactivated is one), or never
// one of this organization's. A removed member's page is read from the
// recycle bin's row (/user-login/:id/deleted, which needs USER delete): who
// removed them and when, how they sign in, their kept roles. Without that
// row (never a member here, or no right to read it) the page says only this.
export const NOT_A_CURRENT_MEMBER = "This person isn't a current member of this organization. If they were removed, you can restore them from the Users recycle bin."

export const PURGE_WORD = 'delete'

// One person of the organization (owner report 2026-10-29), in the house
// layout: the header says who they are and their status, with the page's
// actions (Restore for a removed member; everything else, the removal last
// and in red, in the ⋮); then, for a removed member, the notice card; then
// their details on the left and their roles, groups and courses on the
// right. While they are removed every membership card is read-only (content
// refuses those writes with 409 member.removed too).
export const UserLoginEdit = () => {
  const { userID } = useParamsInt()
  const history = useHistory()
  const ids = { purge: useId() }

  const apiFormUserLogin = useApiFormUserLogin()
  const roleOrgUser = useRoleOrgUser()
  const notification = useNotificationContext()
  const userMenuItems = useUserMenuItems()

  const [notMember, setNotMember] = useState(false)
  // the removed member's recycle-bin row: undefined until read, null when
  // there is none to read
  const [row, setRow] = useState(undefined)
  const [loadError, loadFailure, setLoadError] = useErrorLine()

  const loadRemoved = async () => {
    try {
      setRow((await contentGet(`user-login/${userID}/deleted`)) || null)
    } catch {
      setRow(null)
    }
  }

  // api-new rejects with the response itself (a plain object), and this runs
  // from effects that nobody awaits: an uncaught rejection otherwise, which a
  // browser reports as "Uncaught (in promise) Object".
  const handlePath = async () => {
    try {
      await apiFormUserLogin.get(`${userID}`)
      setNotMember(false)
      setRow(undefined)
      setLoadError(null)
    } catch (err) {
      if (err && err.status === 404) {
        setNotMember(true)
        setLoadError(null)
        await loadRemoved()
        return
      }
      setNotMember(false)
      setLoadError(serverMessage(err, "This person couldn't be loaded."), err)
    }
  }

  const removed = notMember && !!row
  const unknown = notMember && row === null
  const live = !notMember && apiFormUserLogin.form && (apiFormUserLogin.form.email || apiFormUserLogin.form.userID) ? apiFormUserLogin.form : null
  const person = removed ? row : live
  const title = personTitle(person || {})
  const canChange = !!roleOrgUser.accessDelete

  // the dialogs: remove (a live member), restore and delete permanently (a
  // removed one)
  const [dialog, setDialog] = useState(null)
  const [busy, setBusy] = useState(false)
  const [typed, setTyped] = useState('')
  const [actError, actFailure, setActError] = useErrorLine()
  const open = kind => () => { setActError(null); setTyped(''); setDialog(kind) }
  const close = () => { if (!busy) { setDialog(null); setActError(null) } }

  const act = async (fn, after) => {
    if (busy) return
    setBusy(true)
    setActError(null)
    try {
      const result = await fn()
      setBusy(false)
      setDialog(null)
      after(result)
    } catch (err) {
      setBusy(false)
      setActError(dialog === 'remove' ? serverMessage(err, "They couldn't be removed.") : recycleErrorMessage(err, 'They were already restored or deleted.'), err)
    }
  }

  const handleRestore = () => act(() => contentPut(`user-login/${userID}/restore`, {}), result => {
    const notice = restoreNotice(result)
    notification.toast.notify(notice.text, notice.duration)
    handlePath()
  })
  const handleRemove = () => act(() => contentDelete(`user-login/${userID}`), () => {
    notification.toast.notify('Removed from the organization', 3000)
    handlePath()
  })
  const handlePurge = () => act(() => contentDelete(`user-login/${userID}/purge`), () => {
    notification.toast.notify('Deleted permanently', 3000)
    history.push('/org/user/list')
  })

  const words = {
    restore: restoreDialog({ name: title, row }),
    remove: removeDialog({ name: title, managedBy: person && person.managedBy }),
    purge: purgeDialog({ name: title })
  }

  const actions =
    <React.Fragment>
      {removed && canChange &&
        <button type="button" className="btn sw-btn" onClick={open('restore')}>
          <IconRestore className="me-1" />Restore
        </button>}
      <MenuHeaderContext.Provider value={true}>
        <Menu forceDropdown>
          {userMenuItems(userID, {
            removed: notMember,
            onRemove: open('remove'),
            onPurge: removed ? open('purge') : undefined
          })}
        </Menu>
      </MenuHeaderContext.Provider>
    </React.Fragment>

  const current = dialog && words[dialog]

  return (
    <React.Fragment>
      <BreadcrumbView Icon={IconEdit} text={title} onPath={handlePath}>
        <div className="sw-workspace sw-pane pp-page" data-testid="person-page">
          <PersonHeader person={person} removed={removed} unknown={unknown} failed={!!loadError} actions={actions} hasMain={removed && canChange} />

          {loadError && <Notice tone="bad" role="alert">{loadError}<ErrorReference error={loadFailure} /></Notice>}
          {unknown && <div className="mb-3"><Notice tone="warn" role="status">{NOT_A_CURRENT_MEMBER}</Notice></div>}
          {removed && <RemovedCard row={row} canRestore={canChange} />}

          <div className="pp-grid">
            <div className="pp-col">
              {person && <PersonDetails person={person} removed={removed} />}
              {live && <UserInvite userLogin={live} onUpdate={handlePath} />}
              <UserLinkedAccounts userID={userID} />
            </div>

            <div className="pp-col">
              {removed && <KeptNote />}
              {(live || removed) && <RolesSection userID={userID} readOnly={removed} keptRoles={row && row.keptRoles} />}

              <RoleOrgGroup accessRead>
                <RoleOrgGroupUser accessRead>
                  <GroupsSection userID={userID} readOnly={notMember} />
                </RoleOrgGroupUser>
              </RoleOrgGroup>

              <RoleOrgNode accessRead>
                <RoleOrgNodeUser accessRead>
                  <CoursesSection userID={userID} readOnly={notMember} />
                </RoleOrgNodeUser>
              </RoleOrgNode>
            </div>
          </div>
        </div>

        <ConfirmDialog
          open={Boolean(current)}
          title={current ? current.title : ''}
          body={current ? current.body[0] : ''}
          confirmText={busy ? 'Working…' : current ? current.confirm : ''}
          confirmClassName={dialog === 'restore' ? 'btn sw-btn-primary' : 'btn sw-btn-danger'}
          scrim
          busy={busy}
          error={actError}
          failure={actFailure}
          confirmDisabled={dialog === 'purge' && typed.trim().toLowerCase() !== PURGE_WORD}
          onConfirm={dialog === 'restore' ? handleRestore : dialog === 'remove' ? handleRemove : handlePurge}
          onCancel={close}>
          {current && current.body.slice(1).map(line => <p key={line} className="sw-hint" data-testid="dialog-more">{line}</p>)}
          {dialog === 'purge' &&
            <div className="mb-3">
              <label className="sw-label" htmlFor={ids.purge}>Type “{PURGE_WORD}” to confirm</label>
              <input id={ids.purge} className="form-control" autoComplete="off" value={typed} onChange={e => setTyped(e.target.value)} />
            </div>}
        </ConfirmDialog>
      </BreadcrumbView>
      <HeatmapLayoutUser />
    </React.Fragment>
  )
}
